import { useEffect, useState, useCallback } from "react";
import api, { apiErr } from "@/lib/api";
import { PageHeader, Empty } from "@/components/Bits";
import { money, date } from "@/lib/format";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { Plus, Trash2, CheckCircle2, PackageCheck, Lock } from "lucide-react";
import { SuperDelete } from "@/components/SuperDelete";
import { SuperEdit } from "@/components/SuperEdit";
import { jobText } from "@/components/JobTitle";

const PO_STATUS = { draft: "text-slate-400", pending: "text-amber-400", approved: "text-sky-400", part_received: "text-amber-400", received: "text-emerald-400", invoiced: "text-emerald-500" };

export default function Purchasing() {
  return (
    <div className="p-4 sm:p-6 lg:p-8">
      <PageHeader title="Purchasing" subtitle="Supplier POs, goods received and supplier invoices — separate from the customer PO" />
      <Tabs defaultValue="pos">
        <TabsList>
          <TabsTrigger value="pos" data-testid="tab-pos">Supplier POs</TabsTrigger>
          <TabsTrigger value="invoices" data-testid="tab-supplier-invoices">Supplier Invoices</TabsTrigger>
        </TabsList>
        <TabsContent value="pos"><POs /></TabsContent>
        <TabsContent value="invoices"><SupplierInvoices /></TabsContent>
      </Tabs>
    </div>
  );
}

function POs() {
  const { user } = useAuth();
  const [pos, setPos] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [open, setOpen] = useState(false);
  const [supplier, setSupplier] = useState("");
  const newLine = () => ({ job_id: "", description: "", quantity: 1, unit_cost: 0, vat_rate: 23 });
  const [lines, setLines] = useState([newLine()]);
  const net = lines.reduce((s, l) => s + Number(l.quantity || 0) * Number(l.unit_cost || 0), 0);
  const vat = lines.reduce((s, l) => s + Number(l.quantity || 0) * Number(l.unit_cost || 0) * Number(l.vat_rate || 0) / 100, 0);
  const jobsById = Object.fromEntries(jobs.map((j) => [j.id, j]));

  const load = useCallback(() => { api.get("/supplier-pos").then((r) => setPos(r.data)); }, []);
  useEffect(() => { load(); api.get("/jobs").then((r) => setJobs(r.data)); }, [load]);

  const create = async () => {
    try {
      await api.post("/supplier-pos", { supplier, status: "pending", lines });
      toast.success("Supplier PO created");
      setOpen(false); setSupplier(""); setLines([newLine()]); load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const approve = async (po) => {
    try { await api.patch(`/supplier-pos/${po.id}`, { status: "approved" }); toast.success("PO approved"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const receive = async (po) => {
    const grnLines = po.lines.map((l) => ({ po_line_id: l.id, job_id: l.job_id, quantity: l.quantity - (l.qty_received || 0) }));
    await api.post("/goods-received", { po_id: po.id, lines: grnLines });
    toast.success("Goods received recorded"); load();
  };
  const setLine = (i, k, v) => setLines((s) => s.map((l, idx) => (idx === i ? { ...l, [k]: v } : l)));

  return (
    <>
      <div className="flex justify-end my-4">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="new-po-button" className="font-bold uppercase"><Plus className="w-4 h-4 mr-1" />New Supplier PO</Button></DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-heading uppercase">New Supplier PO</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label className="text-xs">Supplier</Label><Input value={supplier} onChange={(e) => setSupplier(e.target.value)} data-testid="po-supplier" className="mt-1" /></div>
              <Label className="text-xs">Lines (each linked to a job)</Label>
              {lines.map((l, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-end border-b border-border pb-2">
                  <div className="col-span-12 sm:col-span-6"><Select value={l.job_id} onValueChange={(v) => setLine(i, "job_id", v)}><SelectTrigger data-testid={`po-line-job-${i}`}><SelectValue placeholder="Customer · Job" /></SelectTrigger>
                    <SelectContent>{jobs.map((j) => <SelectItem key={j.id} value={j.id}>{jobText(j)}</SelectItem>)}</SelectContent></Select></div>
                  <div className="col-span-12 sm:col-span-6"><Input placeholder="Description" value={l.description} onChange={(e) => setLine(i, "description", e.target.value)} data-testid={`po-line-desc-${i}`} /></div>
                  <div className="col-span-3"><Label className="text-[10px]">Qty</Label><Input type="number" value={l.quantity} onChange={(e) => setLine(i, "quantity", e.target.value)} data-testid={`po-line-qty-${i}`} /></div>
                  <div className="col-span-3"><Label className="text-[10px]">€/unit (ex VAT)</Label><Input type="number" value={l.unit_cost} onChange={(e) => setLine(i, "unit_cost", e.target.value)} data-testid={`po-line-cost-${i}`} /></div>
                  <div className="col-span-3"><Label className="text-[10px]">VAT %</Label><Input type="number" value={l.vat_rate} onChange={(e) => setLine(i, "vat_rate", e.target.value)} data-testid={`po-line-vat-${i}`} /></div>
                  <div className="col-span-3 text-right text-xs font-mono-num pb-2">{money(Number(l.quantity || 0) * Number(l.unit_cost || 0) * (1 + Number(l.vat_rate || 0) / 100))}</div>
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={() => setLines((s) => [...s, newLine()])} data-testid="po-add-line"><Plus className="w-3 h-3 mr-1" />Add line</Button>
              <div className="text-sm text-right space-y-0.5" data-testid="po-form-totals"><div>Net {money(net)}</div><div>VAT {money(vat)}</div><div className="font-bold">Gross {money(net + vat)}</div></div>
            </div>
            <DialogFooter><Button onClick={create} disabled={!supplier} data-testid="po-create-submit" className="font-bold uppercase">Create PO</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {pos.length === 0 ? <Empty text="No supplier POs." /> : (
        <div className="space-y-3">
          {pos.map((po) => (
            <div key={po.id} className="card-industrial p-4" data-testid={`po-${po.po_number}`}>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <span className="font-mono-num text-primary font-bold">{po.po_number}</span>
                  <span className="text-sm ml-3">{po.supplier}</span>
                  <span className={`text-xs ml-3 font-mono uppercase ${PO_STATUS[po.status] || "text-muted-foreground"}`}>{po.status.replace("_", " ")}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-right" data-testid={`po-totals-${po.po_number}`}><span className="font-mono-num font-bold">{money(po.total)}</span><span className="text-[10px] text-muted-foreground ml-1">net</span>
                    {po.vat_total != null && <div className="text-[11px] text-muted-foreground font-mono-num">+ VAT {money(po.vat_total)} = {money(po.gross_total)}</div>}</span>
                  <SuperDelete entity="po" id={po.id} label={po.po_number} onDone={() => window.location.reload()} />
                  {["draft", "pending"].includes(po.status) && (
                    (user.approval_limit || 0) >= po.total
                      ? <Button size="sm" variant="outline" onClick={() => approve(po)} data-testid={`po-approve-${po.po_number}`}><CheckCircle2 className="w-4 h-4 mr-1" />Approve</Button>
                      : <span className="text-[11px] text-amber-400 flex items-center gap-1" data-testid={`po-needs-approval-${po.po_number}`}><Lock className="w-3.5 h-3.5" />Needs approval ≥ {money(po.total)}</span>
                  )}
                  {["approved", "part_received"].includes(po.status) && <Button size="sm" variant="outline" onClick={() => receive(po)} data-testid={`po-receive-${po.po_number}`}><PackageCheck className="w-4 h-4 mr-1" />Receive</Button>}
                </div>
              </div>
              <table className="w-full text-xs mt-3">
                <tbody className="divide-y divide-border">
                  {po.lines.map((l) => (
                    <tr key={l.id}><td className="py-1.5 text-muted-foreground">{l.description}{l.job_id && jobsById[l.job_id] && <span className="ml-2 text-[11px]">→ {jobText(jobsById[l.job_id])}</span>}</td>
                    <td className="py-1.5 text-right font-mono-num text-muted-foreground">{l.vat_rate != null ? `VAT ${l.vat_rate}%` : ""}</td>
                    <td className="py-1.5 text-right font-mono-num">{l.qty_received}/{l.quantity} recd</td>
                    <td className="py-1.5 text-right font-mono-num">{money(l.line_total)}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

function SupplierInvoices() {
  const [rows, setRows] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ invoice_number: "", supplier: "", total: 0, vat_rate: 23 });
  const [allocs, setAllocs] = useState([{ job_id: "", amount: 0 }]);

  const load = useCallback(() => { api.get("/supplier-invoices").then((r) => setRows(r.data)); }, []);
  useEffect(() => { load(); api.get("/jobs").then((r) => setJobs(r.data)); }, [load]);

  const create = async () => {
    try {
      await api.post("/supplier-invoices", { ...f, allocations: allocs });
      toast.success("Supplier invoice recorded"); setOpen(false);
      setF({ invoice_number: "", supplier: "", total: 0, vat_rate: 23 }); setAllocs([{ job_id: "", amount: 0 }]); load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const setAlloc = (i, k, v) => setAllocs((s) => s.map((a, idx) => (idx === i ? { ...a, [k]: v } : a)));

  return (
    <>
      <div className="flex justify-end my-4">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="new-supplier-invoice-button" className="font-bold uppercase"><Plus className="w-4 h-4 mr-1" />Record Invoice</Button></DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle className="font-heading uppercase">Supplier Invoice</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Invoice No.</Label><Input value={f.invoice_number} onChange={(e) => setF({ ...f, invoice_number: e.target.value })} data-testid="si-number" className="mt-1" /></div>
                <div><Label className="text-xs">Supplier</Label><Input value={f.supplier} onChange={(e) => setF({ ...f, supplier: e.target.value })} data-testid="si-supplier" className="mt-1" /></div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div><Label className="text-xs">Net total (€)</Label><Input type="number" value={f.total} onChange={(e) => setF({ ...f, total: e.target.value })} data-testid="si-total" className="mt-1" /></div>
                <div><Label className="text-xs">VAT %</Label><Input type="number" value={f.vat_rate ?? 23} onChange={(e) => setF({ ...f, vat_rate: e.target.value })} data-testid="si-vat" className="mt-1" /></div>
                <div><Label className="text-xs">Gross</Label><div className="mt-3 font-mono-num text-sm" data-testid="si-gross">{money(Number(f.total || 0) * (1 + Number(f.vat_rate ?? 23) / 100))}</div></div>
              </div>
              <Label className="text-xs">Allocate across jobs</Label>
              {allocs.map((a, i) => (
                <div key={i} className="grid grid-cols-12 gap-2">
                  <div className="col-span-8"><Select value={a.job_id} onValueChange={(v) => setAlloc(i, "job_id", v)}><SelectTrigger data-testid={`si-alloc-job-${i}`}><SelectValue placeholder="Job" /></SelectTrigger>
                    <SelectContent>{jobs.map((j) => <SelectItem key={j.id} value={j.id}>{jobText(j)}</SelectItem>)}</SelectContent></Select></div>
                  <div className="col-span-4"><Input type="number" placeholder="€" value={a.amount} onChange={(e) => setAlloc(i, "amount", e.target.value)} data-testid={`si-alloc-amount-${i}`} /></div>
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={() => setAllocs((s) => [...s, { job_id: "", amount: 0 }])} data-testid="si-add-alloc"><Plus className="w-3 h-3 mr-1" />Add allocation</Button>
            </div>
            <DialogFooter><Button onClick={create} disabled={!f.invoice_number} data-testid="si-create-submit" className="font-bold uppercase">Record</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      {rows.length === 0 ? <Empty text="No supplier invoices." /> : (
        <div className="card-industrial overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-accent/40 text-[11px] font-mono uppercase tracking-wider text-muted-foreground">
              <tr><th className="text-left px-4 py-3">Invoice</th><th className="text-left px-4 py-3">Supplier</th><th className="text-left px-4 py-3">Date</th>
              <th className="text-left px-4 py-3">Allocated to</th><th className="text-right px-4 py-3">Total</th></tr>
            </thead>
            <tbody className="divide-y divide-border">
              {rows.map((r) => (
                <tr key={r.id} data-testid={`si-${r.invoice_number}`}>
                  <td className="px-4 py-3 font-mono-num font-bold">{r.invoice_number}</td>
                  <td className="px-4 py-3">{r.supplier}</td>
                  <td className="px-4 py-3 text-muted-foreground">{date(r.invoice_date)}</td>
                  <td className="px-4 py-3 text-xs text-muted-foreground">{r.allocations.length} job(s)</td>
                  <td className="px-4 py-3 text-right font-mono-num">{money(r.total)}{r.vat_rate != null && <div className="text-[10px] text-muted-foreground">+{r.vat_rate}% VAT = {money(r.total + (r.vat_amount || 0))}</div>}</td>
                  <td className="px-4 py-3 text-right"><div className="flex justify-end gap-2"><SuperEdit entity="supplier_invoice" record={r} label="supplier invoice" onDone={load} /><SuperDelete entity="supplier_invoice" id={r.id} label={r.invoice_number} onDone={load} /></div></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
